import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Bar, BarChart, Legend,
} from "recharts";
import {
  ShieldCheck, Lock, Euro, TrendingUp, TrendingDown, Award, Building2,
  AlertTriangle, Percent, ExternalLink, Waves, Users, Calendar, Sparkles, Mail, LineChart as LineChartIcon,
  Trash2, RefreshCw, Check, CircleDollarSign, FileText, Download, EyeOff, Eye,
} from "lucide-react";

const PASSCODE_KEY = "poolpass_hq_passcode";

export default function SuperAdmin() {
  const { user, loading } = useApp();
  const nav = useNavigate();
  const [passcode, setPasscode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // hotel object
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [refunds, setRefunds] = useState(null);
  const [busyRefundId, setBusyRefundId] = useState(null);
  const [invoicesData, setInvoicesData] = useState(null);
  const [sendingInvoices, setSendingInvoices] = useState(false);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [togglingDemo, setTogglingDemo] = useState(false);
  const [customers, setCustomers] = useState(null); // {count, customers[]}
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerRoleFilter, setCustomerRoleFilter] = useState("");
  const [deleteCustomer, setDeleteCustomer] = useState(null);
  const [deleteCustomerConfirm, setDeleteCustomerConfirm] = useState("");
  const [deletingCustomer, setDeletingCustomer] = useState(false);

  // Add noindex meta tag while this page is mounted
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive, nosnippet";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "PoolPass · HQ Control";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  // If not logged in as admin, kick out
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth"); return; }
    if (user.role !== "admin") { nav("/"); return; }
    // Try auto-unlock with stored passcode
    const stored = sessionStorage.getItem(PASSCODE_KEY);
    if (stored) {
      verify(stored, true);
    }
  }, [user, loading]); // eslint-disable-line

  const verify = async (code, silent = false) => {
    setVerifying(true);
    try {
      await api.post("/super-admin/verify", null, { headers: { "X-Super-Admin-Passcode": code } });
      sessionStorage.setItem(PASSCODE_KEY, code);
      setUnlocked(true);
      await loadOverview(code);
    } catch (e) {
      if (!silent) toast.error(e.response?.status === 403 ? "Passcode non valida" : "Errore di verifica");
      sessionStorage.removeItem(PASSCODE_KEY);
    } finally {
      setVerifying(false);
    }
  };

  const loadOverview = async (code) => {
    try {
      const [{ data: overview }, { data: trendData }, { data: refundsData }, { data: invData }, { data: settingsData }, { data: custData }] = await Promise.all([
        api.get("/super-admin/overview", { headers: { "X-Super-Admin-Passcode": code } }),
        api.get("/super-admin/monthly-trend", { headers: { "X-Super-Admin-Passcode": code } }),
        api.get("/super-admin/pending-refunds", { headers: { "X-Super-Admin-Passcode": code } }),
        api.get("/super-admin/invoices", { headers: { "X-Super-Admin-Passcode": code } }),
        api.get("/super-admin/settings", { headers: { "X-Super-Admin-Passcode": code } }),
        api.get("/super-admin/customers", { headers: { "X-Super-Admin-Passcode": code } }),
      ]);
      setData(overview);
      setTrend(trendData);
      setRefunds(refundsData);
      setInvoicesData(invData);
      setPlatformSettings(settingsData);
      setCustomers(custData);
    } catch (e) {
      toast.error("Impossibile caricare i dati");
    }
  };

  const searchCustomers = async () => {
    const code = sessionStorage.getItem(PASSCODE_KEY);
    try {
      const params = {};
      if (customerQuery) params.q = customerQuery;
      if (customerRoleFilter) params.role = customerRoleFilter;
      const { data } = await api.get("/super-admin/customers", { headers: { "X-Super-Admin-Passcode": code }, params });
      setCustomers(data);
    } catch { toast.error("Ricerca fallita"); }
  };

  const confirmDeleteCustomer = async () => {
    if (!deleteCustomer) return;
    if (deleteCustomerConfirm.trim().toLowerCase() !== deleteCustomer.email.toLowerCase()) {
      toast.error("Email di conferma non corrispondente");
      return;
    }
    setDeletingCustomer(true);
    const code = sessionStorage.getItem(PASSCODE_KEY);
    try {
      const { data } = await api.delete(`/super-admin/customers/${deleteCustomer.user_id}`, { headers: { "X-Super-Admin-Passcode": code } });
      const bits = [];
      if (data.refunded) bits.push(`${data.refunded} rimborsi`);
      if (data.bookings_removed) bits.push(`${data.bookings_removed} prenot.`);
      if (data.pools_removed) bits.push(`${data.pools_removed} piscine`);
      toast.success(`Utente eliminato · ${bits.join(" · ") || "nessun dato residuo"}`);
      setDeleteCustomer(null);
      setDeleteCustomerConfirm("");
      await searchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione fallita");
    } finally {
      setDeletingCustomer(false);
    }
  };

  const toggleHideDemo = async () => {
    setTogglingDemo(true);
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      const next = !platformSettings?.hide_demo_hotels;
      await api.patch("/super-admin/settings", { hide_demo_hotels: next }, { headers: { "X-Super-Admin-Passcode": code } });
      toast.success(next ? "Hotel demo nascosti dalla piattaforma" : "Hotel demo tornati visibili");
      const { data } = await api.get("/super-admin/settings", { headers: { "X-Super-Admin-Passcode": code } });
      setPlatformSettings(data);
    } catch (e) {
      toast.error("Toggle fallito");
    } finally {
      setTogglingDemo(false);
    }
  };

  const sendMonthlyInvoicesNow = async () => {
    setSendingInvoices(true);
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      // Trigger for previous month (default when body has null year/month)
      const { data: r } = await api.post("/super-admin/send-monthly-invoices-now", {}, { headers: { "X-Super-Admin-Passcode": code } });
      toast.success(`Fatture: ${r.generated} generate · ${r.skipped} già esistenti (${r.period_label})`);
      const { data: invData } = await api.get("/super-admin/invoices", { headers: { "X-Super-Admin-Passcode": code } });
      setInvoicesData(invData);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generazione fatture fallita");
    } finally {
      setSendingInvoices(false);
    }
  };

  const downloadInvoice = async (invoiceId, number) => {
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/invoices/${invoiceId}/download`, {
        headers: { Authorization: `Bearer ${token}`, "X-Super-Admin-Passcode": code },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Download fattura fallito");
    }
  };

  const reloadRefunds = async () => {
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      const { data } = await api.get("/super-admin/pending-refunds", { headers: { "X-Super-Admin-Passcode": code } });
      setRefunds(data);
    } catch {}
  };

  const retryRefund = async (bookingId) => {
    setBusyRefundId(bookingId);
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      await api.post(`/super-admin/retry-refund/${bookingId}`, null, { headers: { "X-Super-Admin-Passcode": code } });
      toast.success("Refund Stripe eseguito");
      await reloadRefunds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry fallito");
    } finally {
      setBusyRefundId(null);
    }
  };

  const markRefunded = async (bookingId) => {
    setBusyRefundId(bookingId);
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      await api.post(`/super-admin/mark-refunded/${bookingId}`, null, { headers: { "X-Super-Admin-Passcode": code } });
      toast.success("Segnato come rimborsato manualmente");
      await reloadRefunds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Azione fallita");
    } finally {
      setBusyRefundId(null);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      const { data: resp } = await api.post("/super-admin/send-digest-now", null, { headers: { "X-Super-Admin-Passcode": code } });
      toast.success(`Digest inviato a ${resp.recipient}`);
    } catch (e) {
      toast.error("Invio digest fallito");
    } finally {
      setSendingDigest(false);
    }
  };

  const confirmDeleteHotel = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmName.trim() !== deleteTarget.hotel_name) {
      toast.error("Il nome non corrisponde");
      return;
    }
    setDeleting(true);
    try {
      const code = sessionStorage.getItem(PASSCODE_KEY);
      const { data: resp } = await api.delete(`/super-admin/hotel/${deleteTarget.owner_id}`, { headers: { "X-Super-Admin-Passcode": code } });
      toast.success(`Hotel eliminato · ${resp.pools_removed} piscine · ${resp.bookings_removed} prenotazioni rimosse`);
      setDeleteTarget(null);
      setDeleteConfirmName("");
      await loadOverview(code);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione fallita");
    } finally {
      setDeleting(false);
    }
  };

  const lock = () => {
    sessionStorage.removeItem(PASSCODE_KEY);
    setUnlocked(false);
    setPasscode("");
    setData(null);
  };

  if (loading || !user || user.role !== "admin") return null;

  if (!unlocked) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white border border-border rounded-3xl p-10 shadow-xl">
          <div className="w-14 h-14 rounded-2xl bg-[#005C7A] flex items-center justify-center mb-5">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <div className="overline text-[#E27B58] mb-2">POOLPASS HQ · CONTROL ROOM</div>
          <h1 className="font-display text-3xl font-bold mb-2">Accesso riservato</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Sei loggato come <strong>{user.email}</strong>. Inserisci la passcode HQ per accedere alla vista globale della piattaforma.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); verify(passcode); }} className="space-y-4">
            <div>
              <Label className="flex items-center gap-1.5 mb-1.5"><Lock className="w-3.5 h-3.5" /> HQ Passcode</Label>
              <Input
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="••••••••••••••"
                autoFocus
                autoComplete="off"
                data-testid="hq-passcode-input"
              />
            </div>
            <Button
              type="submit"
              disabled={verifying || !passcode}
              className="w-full rounded-full bg-[#005C7A] hover:bg-[#004A63] text-white"
              data-testid="hq-passcode-submit"
            >
              {verifying ? "Verifica..." : "Sblocca HQ"}
            </Button>
          </form>
          <div className="mt-6 pt-4 border-t border-border text-xs text-muted-foreground">
            Questa area non è indicizzata sui motori di ricerca. Ogni accesso è tracciato.
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="max-w-7xl mx-auto px-6 py-16 text-center text-muted-foreground">Caricamento dati globali…</div>;
  }

  const T = data.totals;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="overline text-[#E27B58] mb-2 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> POOLPASS HQ CONTROL
          </div>
          <h1 className="font-display text-4xl font-bold">Super Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Visione globale su hotel, prenotazioni e commissioni.</p>
        </div>
        <Button variant="outline" onClick={lock} className="rounded-full" data-testid="hq-lock">
          <Lock className="w-3.5 h-3.5 mr-2" /> Blocca sessione
        </Button>
      </div>

      {/* Demo hotels toggle — hide seeded hotels until real ones sign up */}
      {platformSettings && (
        <div className={`mt-6 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3 border-2 ${platformSettings.hide_demo_hotels ? "bg-purple-50 border-purple-300" : "bg-white border-border"}`} data-testid="demo-toggle-card">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${platformSettings.hide_demo_hotels ? "bg-purple-200" : "bg-muted"}`}>
              {platformSettings.hide_demo_hotels ? <EyeOff className="w-5 h-5 text-purple-700" /> : <Eye className="w-5 h-5 text-muted-foreground" />}
            </div>
            <div>
              <div className="font-semibold flex items-center gap-2">
                Hotel demo {platformSettings.hide_demo_hotels ? "nascosti" : "visibili"}
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">{platformSettings.demo_pools_count} demo · {platformSettings.real_pools_count} veri</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {platformSettings.hide_demo_hotels
                  ? "I 26 hotel seedati sono invisibili al pubblico. Solo hotel realmente registrati appaiono su mappa, ricerca e trending."
                  : "I hotel demo seedati sono visibili sulla piattaforma. Attiva per nasconderli quando avrai hotel reali iscritti."}
              </div>
            </div>
          </div>
          <Button
            onClick={toggleHideDemo}
            disabled={togglingDemo}
            className={`rounded-full ${platformSettings.hide_demo_hotels ? "bg-purple-600 hover:bg-purple-700" : "bg-[#005C7A] hover:bg-[#004A63]"}`}
            data-testid="demo-toggle-btn"
          >
            {togglingDemo ? "…" : (platformSettings.hide_demo_hotels ? "Riattiva hotel demo" : "Nascondi hotel demo")}
          </Button>
        </div>
      )}

      {/* Weekly Digest CTA */}
      <div className="mt-6 bg-gradient-to-r from-[#005C7A] to-[#004A63] text-white rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid="digest-cta">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold">Digest settimanale automatico</div>
            <div className="text-xs text-white/75">Ogni lunedì alle 08:00 UTC ricevi email con volume, top 3 hotel e i 3 hotel a rischio.</div>
          </div>
        </div>
        <Button
          onClick={sendDigestNow}
          disabled={sendingDigest}
          className="rounded-full bg-white text-[#005C7A] hover:bg-white/90"
          data-testid="hq-send-digest-now"
        >
          <Mail className="w-3.5 h-3.5 mr-2" /> {sendingDigest ? "Invio…" : "Invia ora (test)"}
        </Button>
      </div>

      {/* Global KPIs */}
      <div className="grid md:grid-cols-3 gap-4 mt-8 mb-6">
        <div className="bg-white border border-border rounded-2xl p-6" data-testid="kpi-gross">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2"><TrendingUp className="w-3.5 h-3.5" /> Volume totale prenotazioni</div>
          <div className="font-display text-4xl font-bold text-[#005C7A]">€{T.total_gross.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-2">{T.bookings_count} prenotazioni pagate</div>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6" data-testid="kpi-owners">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2"><Building2 className="w-3.5 h-3.5" /> Guadagno hotel (netto)</div>
          <div className="font-display text-4xl font-bold text-[#1A202C]">€{T.total_owners_earnings.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-2">{T.hotels_count} hotel · {T.pools_count} piscine</div>
        </div>
        <div className="bg-gradient-to-br from-[#E27B58] to-[#C56A4A] text-white rounded-2xl p-6" data-testid="kpi-commission">
          <div className="flex items-center gap-2 text-white/80 text-xs mb-2"><Percent className="w-3.5 h-3.5" /> Commissioni piattaforma (10%)</div>
          <div className="font-display text-4xl font-bold">€{T.platform_commission.toFixed(2)}</div>
          <div className="text-xs text-white/80 mt-2">Ricavo totale PoolPass</div>
        </div>
      </div>

      <Tabs defaultValue="hotels">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="hotels" data-testid="tab-hq-hotels"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Hotel</TabsTrigger>
          <TabsTrigger value="trend" data-testid="tab-hq-trend"><LineChartIcon className="w-3.5 h-3.5 mr-1.5" /> Trend storico</TabsTrigger>
          <TabsTrigger value="ranking" data-testid="tab-hq-ranking"><Award className="w-3.5 h-3.5 mr-1.5" /> Classifica</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-hq-perf">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Performance
            {data.low_performers.length > 0 && <span className="ml-1.5 bg-red-500 text-white text-xs px-1.5 rounded-full">{data.low_performers.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-hq-refunds">
            <CircleDollarSign className="w-3.5 h-3.5 mr-1.5" /> Rimborsi
            {refunds?.count > 0 && <span className="ml-1.5 bg-amber-500 text-white text-xs px-1.5 rounded-full">{refunds.count}</span>}
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-hq-invoices">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Fatture
            {invoicesData?.count > 0 && <span className="ml-1.5 bg-[#005C7A] text-white text-xs px-1.5 rounded-full">{invoicesData.count}</span>}
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-hq-customers">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Clienti
            {customers?.count > 0 && <span className="ml-1.5 bg-[#635BFF] text-white text-xs px-1.5 rounded-full">{customers.count}</span>}
          </TabsTrigger>
        </TabsList>

        {/* HOTELS TAB */}
        <TabsContent value="hotels">
          <div className="bg-white border border-border rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-3">Hotel</th>
                  <th className="text-left px-4 py-3">Owner</th>
                  <th className="text-right px-4 py-3">Piscine</th>
                  <th className="text-right px-4 py-3">Prenot.</th>
                  <th className="text-right px-4 py-3">30gg</th>
                  <th className="text-right px-4 py-3">Volume</th>
                  <th className="text-right px-4 py-3">Netto hotel</th>
                  <th className="text-right px-4 py-3">Commiss.</th>
                  <th className="text-left px-4 py-3">Schede</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {data.hotels.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-10 text-muted-foreground">Nessun hotel registrato ancora.</td></tr>
                )}
                {data.hotels.map((h) => (
                  <tr key={h.owner_id} className="border-t border-border hover:bg-muted/20" data-testid={`hq-hotel-${h.owner_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold flex items-center gap-2">
                        {h.hotel_name}
                        {h.suspended && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase tracking-wide">Sospeso</span>}
                      </div>
                      {h.has_daily_offer && <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 mr-1">Offerta attiva</span>}
                      {h.has_discount && <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">Sconto attivo</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{h.owner_name}</div>
                      <div className="text-muted-foreground">{h.owner_email}</div>
                    </td>
                    <td className="px-4 py-3 text-right">{h.pools_count}</td>
                    <td className="px-4 py-3 text-right font-semibold">{h.bookings}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={h.bookings_30d === 0 ? "text-red-600 font-semibold" : ""}>{h.bookings_30d}</span>
                    </td>
                    <td className="px-4 py-3 text-right">€{h.gross.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-[#005C7A] font-semibold">€{h.net_to_owner.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-[#E27B58] font-semibold">€{h.commission.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {h.pools.map((p) => (
                          <Link
                            key={p.pool_id}
                            to={`/pool/${p.pool_id}`}
                            target="_blank"
                            rel="noopener"
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#005C7A]/10 text-[#005C7A] hover:bg-[#005C7A]/20"
                            data-testid={`hq-pool-link-${p.pool_id}`}
                          >
                            <Waves className="w-3 h-3" /> {p.title.length > 22 ? p.title.slice(0, 22) + "…" : p.title}
                            <ExternalLink className="w-2.5 h-2.5" />
                          </Link>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setDeleteTarget(h); setDeleteConfirmName(""); }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Elimina hotel"
                        data-testid={`hq-delete-hotel-${h.owner_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Delete hotel confirm dialog */}
          <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
            <AlertDialogContent data-testid="hq-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="w-5 h-5" /> Eliminare l'hotel?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 pt-2">
                    <p className="text-sm">Stai per eliminare <strong className="text-[#1A202C]">{deleteTarget?.hotel_name}</strong> ({deleteTarget?.owner_email}).</p>
                    <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 text-xs text-red-900 space-y-1">
                      <div>Verranno rimossi in cascata:</div>
                      <div>· <strong>{deleteTarget?.pools_count}</strong> piscine</div>
                      <div>· <strong>{deleteTarget?.bookings}</strong> prenotazioni</div>
                      <div>· tutte le recensioni, favoriti, messaggi e foto associati</div>
                      <div>· l'account del gestore</div>
                    </div>
                    <p className="text-sm font-semibold text-red-700">Questa azione è irreversibile.</p>
                    <div>
                      <Label className="text-xs">Per confermare, digita il nome esatto dell'hotel:</Label>
                      <Input
                        value={deleteConfirmName}
                        onChange={(e) => setDeleteConfirmName(e.target.value)}
                        placeholder={deleteTarget?.hotel_name}
                        autoComplete="off"
                        data-testid="hq-delete-confirm-input"
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="hq-delete-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); confirmDeleteHotel(); }}
                  disabled={deleting || deleteConfirmName.trim() !== deleteTarget?.hotel_name}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="hq-delete-confirm"
                >
                  {deleting ? "Eliminazione…" : "Elimina definitivamente"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* TREND TAB */}
        <TabsContent value="trend">
          <div className="bg-white border border-border rounded-2xl p-6" data-testid="hq-trend-chart">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2 text-[#005C7A] text-xs uppercase tracking-wide font-semibold mb-1">
                  <LineChartIcon className="w-3.5 h-3.5" /> Andamento mensile
                </div>
                <h2 className="font-display text-xl font-semibold">Prenotazioni & Fatturato · ultimi 12 mesi</h2>
                <p className="text-sm text-muted-foreground mt-1">Individua trend stagionali (alta estate, calo invernale) per pianificare campagne mirate.</p>
              </div>
            </div>

            {!trend || trend.months.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Nessun dato disponibile.</div>
            ) : (
              <>
                <div className="h-64 mb-8">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend.months} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                      <YAxis yAxisId="left" stroke="#005C7A" fontSize={11} />
                      <YAxis yAxisId="right" orientation="right" stroke="#E27B58" fontSize={11} />
                      <Tooltip
                        formatter={(v, name) => [name === "Fatturato" ? `€${v.toFixed(2)}` : v, name]}
                        contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                      <Line yAxisId="left" type="monotone" dataKey="bookings" name="Prenotazioni" stroke="#005C7A" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line yAxisId="right" type="monotone" dataKey="gross" name="Fatturato" stroke="#E27B58" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <h3 className="font-display text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Commissioni piattaforma per mese</h3>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trend.months} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                      <YAxis stroke="#94A3B8" fontSize={11} />
                      <Tooltip formatter={(v) => [`€${v.toFixed(2)}`, "Commissione"]} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                      <Bar dataKey="commission" name="Commissioni" fill="#E27B58" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {trend.months.every((m) => m.bookings === 0) && (
                  <div className="mt-6 bg-muted/30 rounded-xl p-4 text-sm text-muted-foreground text-center">
                    Nessuna prenotazione registrata negli ultimi 12 mesi. Il grafico si popolerà automaticamente non appena arriveranno le prime prenotazioni.
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>


        {/* RANKING TAB */}
        <TabsContent value="ranking">
          <div className="grid md:grid-cols-2 gap-6">
            <RankCard
              icon={<Calendar className="w-4 h-4" />}
              title="Top 10 per numero di prenotazioni"
              subtitle="Gli hotel più venduti in assoluto."
              rows={data.top_by_bookings}
              metric={(h) => `${h.bookings} prenot.`}
              accent="#005C7A"
              testid="rank-bookings"
            />
            <RankCard
              icon={<Euro className="w-4 h-4" />}
              title="Top 10 per fatturato"
              subtitle="Ranked per volume totale (lordo)."
              rows={data.top_by_revenue}
              metric={(h) => `€${h.gross.toFixed(2)}`}
              accent="#E27B58"
              testid="rank-revenue"
            />
          </div>
        </TabsContent>

        {/* PERFORMANCE TAB */}
        <TabsContent value="performance">
          <div className="bg-white border border-border rounded-2xl p-6 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E27B58]/15 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-[#E27B58]" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold">Radar performance</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Identifica gli hotel che vendono meno per suggerire loro <strong>sconti</strong> o l'attivazione del banner <strong>"Offerta del Giorno"</strong>.
                </p>
              </div>
            </div>
          </div>

          {data.low_performers.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 text-green-600" />
              <div className="font-semibold">Tutti gli hotel stanno vendendo bene 🎉</div>
              <div className="text-sm text-muted-foreground mt-1">Nessuna anomalia rilevata.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {data.low_performers.map((h) => (
                <div key={h.owner_id} className="bg-white border-2 border-red-100 rounded-2xl p-5" data-testid={`low-perf-${h.owner_id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingDown className="w-4 h-4 text-red-600" />
                        <span className="font-display text-lg font-semibold">{h.hotel_name}</span>
                        {h.bookings_30d === 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">0 prenot. 30gg</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{h.owner_name} · {h.owner_email}</div>
                    </div>
                    <div className="text-right text-xs">
                      <div>{h.bookings} tot. · {h.bookings_30d} negli ultimi 30gg</div>
                      <div className="text-muted-foreground">€{h.gross.toFixed(2)} volume</div>
                    </div>
                  </div>
                  <div className="bg-[#E27B58]/10 border-l-4 border-[#E27B58] rounded-r-lg px-4 py-3 text-sm">
                    <div className="font-semibold text-[#C56A4A] mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Suggerimento</div>
                    <div className="text-[#1A202C]">{h.suggestion}</div>
                    <div className="mt-2 flex items-center gap-1.5 text-xs">
                      {!h.has_daily_offer && <span className="px-2 py-0.5 rounded bg-white border border-[#E27B58]/30">Manca banner offerta</span>}
                      {!h.has_discount && <span className="px-2 py-0.5 rounded bg-white border border-[#E27B58]/30">Nessuno sconto attivo</span>}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {h.pools.map((p) => (
                      <Link
                        key={p.pool_id}
                        to={`/pool/${p.pool_id}`}
                        target="_blank"
                        rel="noopener"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted hover:bg-[#005C7A]/10 hover:text-[#005C7A]"
                      >
                        <Waves className="w-3 h-3" /> {p.title}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* REFUNDS TAB */}
        <TabsContent value="refunds">
          <div className="bg-white border border-border rounded-2xl p-6 mb-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <CircleDollarSign className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold">Rimborsi in corso</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Prenotazioni pagate per cui il rimborso automatico Stripe è <strong>pendente</strong> (sospensione/eliminazione hotel). Ritenta il refund automatico o segna manualmente come rimborsato dopo intervento diretto su Stripe.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Totale pendente</div>
                  <div className="font-display text-lg font-bold text-amber-700">€{(refunds?.total_amount || 0).toFixed(2)}</div>
                </div>
                <Button variant="outline" size="sm" onClick={reloadRefunds} className="rounded-full" data-testid="refunds-refresh">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Aggiorna
                </Button>
              </div>
            </div>
          </div>

          {!refunds || refunds.count === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="refunds-empty">
              <Check className="w-10 h-10 mx-auto mb-3 text-green-600" />
              <div className="font-semibold">Tutto sotto controllo 🎉</div>
              <div className="text-sm text-muted-foreground mt-1">Nessun rimborso pendente in questo momento.</div>
            </div>
          ) : (
            <div className="bg-white border border-border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3">Booking / Data</th>
                    <th className="text-left px-4 py-3">Hotel / Piscina</th>
                    <th className="text-left px-4 py-3">Guest</th>
                    <th className="text-right px-4 py-3">Importo</th>
                    <th className="text-left px-4 py-3">Motivo</th>
                    <th className="text-left px-4 py-3">Errore</th>
                    <th className="text-right px-4 py-3">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.refunds.map((r) => (
                    <tr key={r.booking_id} className="border-t border-border hover:bg-muted/20" data-testid={`refund-row-${r.booking_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono text-[11px] text-muted-foreground">{r.booking_id}</div>
                        <div className="text-xs mt-0.5">{r.date} · {r.slot === "half" ? "½ giornata" : "giornata intera"}</div>
                        <div className="text-[10px] text-muted-foreground">Sospeso: {r.refunded_at?.slice(0, 10)}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="font-semibold">{r.hotel_name}</div>
                        <div className="text-muted-foreground">{r.pool_title}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div className="font-semibold">{r.guest_name || "—"}</div>
                        <div className="text-muted-foreground">{r.guest_email}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-display font-bold text-amber-700">€{r.amount.toFixed(2)}</div>
                        <div className="text-[10px] text-muted-foreground uppercase">{r.provider}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${r.refund_reason === "hotel_deleted" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.refund_reason === "hotel_deleted" ? "Hotel eliminato" : "Hotel sospeso"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-red-600 max-w-[220px] truncate" title={r.refund_error || ""}>
                        {r.refund_error || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          {r.stripe_session_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyRefundId === r.booking_id}
                              onClick={() => retryRefund(r.booking_id)}
                              className="text-[11px] h-8 px-2.5"
                              data-testid={`refund-retry-${r.booking_id}`}
                              title="Ritenta il refund Stripe automatico"
                            >
                              <RefreshCw className={`w-3 h-3 mr-1 ${busyRefundId === r.booking_id ? "animate-spin" : ""}`} /> Retry
                            </Button>
                          )}
                          <Button
                            size="sm"
                            disabled={busyRefundId === r.booking_id}
                            onClick={() => markRefunded(r.booking_id)}
                            className="text-[11px] h-8 px-2.5 bg-green-600 hover:bg-green-700"
                            data-testid={`refund-manual-${r.booking_id}`}
                            title="Segna come rimborsato dopo intervento manuale"
                          >
                            <Check className="w-3 h-3 mr-1" /> Manuale
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* INVOICES TAB */}
        <TabsContent value="invoices">
          <div className="bg-white border border-border rounded-2xl p-6 mb-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#005C7A]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[#005C7A]" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold">Fatture emesse</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fatture mensili di commissioni PoolPass (IVA 22%) generate automaticamente il <strong>1° di ogni mese</strong> alle 04:00 UTC. Ogni fattura viene inviata via email al gestore con link al PDF.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {invoicesData && (
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Totale fatturato</div>
                    <div className="font-display text-lg font-bold text-[#005C7A]">€{(invoicesData.total_billed || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-muted-foreground">di cui IVA €{(invoicesData.total_iva || 0).toFixed(2)}</div>
                  </div>
                )}
                <Button
                  onClick={sendMonthlyInvoicesNow}
                  disabled={sendingInvoices}
                  className="rounded-full bg-[#E27B58] hover:bg-[#C56A4A]"
                  data-testid="hq-send-invoices-now"
                  title="Genera le fatture del mese scorso (idempotente: salta quelle già create)"
                >
                  <FileText className="w-3.5 h-3.5 mr-2" /> {sendingInvoices ? "Generazione…" : "Genera fatture del mese scorso"}
                </Button>
              </div>
            </div>
          </div>

          {!invoicesData || invoicesData.count === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="invoices-hq-empty">
              <FileText className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <div className="font-semibold">Nessuna fattura emessa ancora</div>
              <div className="text-sm text-muted-foreground mt-1">Clicca "Genera fatture del mese scorso" per creare quelle di questo periodo.</div>
            </div>
          ) : (
            <div className="bg-white border border-border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3">Numero</th>
                    <th className="text-left px-4 py-3">Hotel</th>
                    <th className="text-left px-4 py-3">Periodo</th>
                    <th className="text-right px-4 py-3">Prenot.</th>
                    <th className="text-right px-4 py-3">Volume</th>
                    <th className="text-right px-4 py-3">Imponib.</th>
                    <th className="text-right px-4 py-3">IVA</th>
                    <th className="text-right px-4 py-3">Totale</th>
                    <th className="text-left px-4 py-3">Stato</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoicesData.invoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-t border-border hover:bg-muted/20" data-testid={`hq-invoice-${inv.invoice_id}`}>
                      <td className="px-4 py-3 font-mono text-xs font-semibold">{inv.number}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{inv.hotel_name}</div>
                        <div className="text-[11px] text-muted-foreground">{inv.owner_email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">{inv.period_label}</td>
                      <td className="px-4 py-3 text-right">{inv.bookings_count}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">€{inv.gross_volume.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">€{inv.taxable.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#E27B58]">€{inv.iva.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-display font-bold text-[#005C7A]">€{inv.total.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {inv.sent ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">✓ Inviata</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Non inviata</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadInvoice(inv.invoice_id, inv.number)}
                          className="text-[11px] h-8 px-3"
                          data-testid={`hq-invoice-download-${inv.invoice_id}`}
                        >
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* CUSTOMERS TAB */}
        <TabsContent value="customers">
          <div className="bg-white border border-border rounded-2xl p-5 mb-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-[#635BFF]" />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold">Clienti registrati</h2>
                  <p className="text-sm text-muted-foreground mt-1">Cerca, ispeziona ed elimina clienti (guest) e proprietari (owner). L'eliminazione è definitiva e <strong>rimborsa le prenotazioni future già pagate</strong>.</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Totale</div>
                <div className="font-display text-lg font-bold text-[#635BFF]">{customers?.count ?? 0}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-5 flex-wrap">
              <Input
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchCustomers()}
                placeholder="Cerca per email o nome…"
                className="max-w-sm"
                data-testid="hq-customer-search"
              />
              <select
                value={customerRoleFilter}
                onChange={(e) => setCustomerRoleFilter(e.target.value)}
                className="h-10 rounded-md border border-border bg-white px-3 text-sm"
                data-testid="hq-customer-role-filter"
              >
                <option value="">Tutti</option>
                <option value="guest">Solo ospiti</option>
                <option value="owner">Solo hotel</option>
              </select>
              <Button onClick={searchCustomers} className="rounded-full bg-[#005C7A] hover:bg-[#004A63]" data-testid="hq-customer-apply">Applica</Button>
              <Button variant="outline" onClick={() => { setCustomerQuery(""); setCustomerRoleFilter(""); setTimeout(searchCustomers, 0); }} className="rounded-full">Reset</Button>
            </div>
          </div>

          {!customers || customers.count === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="hq-customers-empty">
              <Users className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <div className="font-semibold">Nessun cliente trovato</div>
              <div className="text-sm text-muted-foreground mt-1">Prova a modificare i filtri di ricerca.</div>
            </div>
          ) : (
            <div className="bg-white border border-border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3">Nome</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Ruolo</th>
                    <th className="text-right px-4 py-3">Prenot.</th>
                    <th className="text-right px-4 py-3">Speso</th>
                    <th className="text-left px-4 py-3">Registrato</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {customers.customers.map((c) => (
                    <tr key={c.user_id} className="border-t border-border hover:bg-muted/20" data-testid={`hq-customer-${c.user_id}`}>
                      <td className="px-4 py-3 font-semibold">{c.name}</td>
                      <td className="px-4 py-3 font-mono text-xs">{c.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${c.role === "owner" ? "bg-[#005C7A]/10 text-[#005C7A]" : "bg-[#635BFF]/10 text-[#635BFF]"}`}>
                          {c.role === "owner" ? "Hotel" : "Ospite"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">{c.bookings}</td>
                      <td className="px-4 py-3 text-right font-display font-bold text-[#005C7A]">€{c.total_spent.toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{c.created_at ? new Date(c.created_at).toLocaleDateString("it-IT") : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setDeleteCustomer(c); setDeleteCustomerConfirm(""); }}
                          className="text-[11px] h-8 px-3 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-500"
                          data-testid={`hq-customer-delete-${c.user_id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Elimina
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

      </Tabs>

      {/* Customer delete confirm dialog */}
      <AlertDialog open={!!deleteCustomer} onOpenChange={(open) => { if (!open) { setDeleteCustomer(null); setDeleteCustomerConfirm(""); } }}>
        <AlertDialogContent data-testid="hq-customer-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Elimina definitivamente questo cliente?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div>Stai per eliminare <strong>{deleteCustomer?.name}</strong> ({deleteCustomer?.email}) e tutti i suoi dati:</div>
                <ul className="list-disc pl-5 text-xs text-muted-foreground">
                  <li>Prenotazioni · Recensioni · Preferiti · Messaggi</li>
                  {deleteCustomer?.role === "owner" && <li className="text-red-700">Tutte le piscine e file dell'hotel</li>}
                  <li className="text-amber-700"><strong>Le prenotazioni future già pagate verranno rimborsate automaticamente via Stripe</strong></li>
                </ul>
                <div className="pt-1">Per confermare, digita l'email del cliente:</div>
                <Input
                  value={deleteCustomerConfirm}
                  onChange={(e) => setDeleteCustomerConfirm(e.target.value)}
                  placeholder={deleteCustomer?.email}
                  className="font-mono text-xs"
                  data-testid="hq-customer-delete-confirm-input"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingCustomer}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={deletingCustomer || deleteCustomerConfirm.trim().toLowerCase() !== (deleteCustomer?.email || "").toLowerCase()}
              onClick={(e) => { e.preventDefault(); confirmDeleteCustomer(); }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="hq-customer-delete-confirm-btn"
            >
              {deletingCustomer ? "Eliminazione…" : "Elimina definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RankCard({ icon, title, subtitle, rows, metric, accent, testid }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1" style={{ color: accent }}>{icon}<span className="text-xs uppercase tracking-wide font-semibold">Ranking</span></div>
      <h2 className="font-display text-xl font-semibold mb-1">{title}</h2>
      <p className="text-xs text-muted-foreground mb-4">{subtitle}</p>
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">Nessun dato ancora.</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((h, i) => (
            <li key={h.owner_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/40">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                style={{ background: i < 3 ? accent : "#94A3B8" }}
              >
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{h.hotel_name}</div>
                <div className="text-xs text-muted-foreground truncate">{h.pools_count} piscine · {h.owner_email}</div>
              </div>
              <div className="font-display text-sm font-bold" style={{ color: accent }}>{metric(h)}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
